import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Search, BookOpen, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { cn, formatCurrency } from '../../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const accountTypeColors = {
  asset: 'bg-blue-100 text-blue-700',
  liability: 'bg-red-100 text-red-700',
  equity: 'bg-purple-100 text-purple-700',
  revenue: 'bg-emerald-100 text-emerald-700',
  expense: 'bg-amber-100 text-amber-700'
};

const ChartOfAccounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    account_type: '',
    description: ''
  });

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      const response = await axios.get(`${API_URL}/finance/accounts`);
      setAccounts(response.data);
    } catch (error) {
      console.error('Failed to fetch accounts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/finance/accounts`, formData);
      fetchAccounts();
      setDialogOpen(false);
      setFormData({ code: '', name: '', account_type: '', description: '' });
    } catch (error) {
      console.error('Failed to create account:', error);
    }
  };

  const filteredAccounts = accounts.filter(account =>
    account.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    account.code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const groupedAccounts = {
    asset: filteredAccounts.filter(a => a.account_type === 'asset'),
    liability: filteredAccounts.filter(a => a.account_type === 'liability'),
    equity: filteredAccounts.filter(a => a.account_type === 'equity'),
    revenue: filteredAccounts.filter(a => a.account_type === 'revenue'),
    expense: filteredAccounts.filter(a => a.account_type === 'expense')
  };

  const totals = {
    asset: groupedAccounts.asset.reduce((sum, a) => sum + a.balance, 0),
    liability: groupedAccounts.liability.reduce((sum, a) => sum + a.balance, 0),
    equity: groupedAccounts.equity.reduce((sum, a) => sum + a.balance, 0),
    revenue: groupedAccounts.revenue.reduce((sum, a) => sum + a.balance, 0),
    expense: groupedAccounts.expense.reduce((sum, a) => sum + a.balance, 0)
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-10 bg-slate-200 rounded animate-pulse w-48" />
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="space-y-4">
            {[...Array(10)].map((_, i) => (
              <div key={i} className="h-10 bg-slate-200 rounded animate-pulse" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="chart-of-accounts-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Chart of Accounts</h1>
          <p className="text-slate-600 mt-1">Manage your accounting chart of accounts</p>
        </div>
        <Button 
          onClick={() => setDialogOpen(true)}
          className="bg-indigo-600 hover:bg-indigo-700 text-white"
          data-testid="add-account-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Account
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-4 h-4 text-blue-600" />
            <span className="text-sm text-slate-500">Assets</span>
          </div>
          <p className="text-xl font-bold text-slate-900 font-mono">{formatCurrency(totals.asset)}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-2 mb-2">
            <TrendingDown className="w-4 h-4 text-red-600" />
            <span className="text-sm text-slate-500">Liabilities</span>
          </div>
          <p className="text-xl font-bold text-slate-900 font-mono">{formatCurrency(totals.liability)}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-2 mb-2">
            <Minus className="w-4 h-4 text-purple-600" />
            <span className="text-sm text-slate-500">Equity</span>
          </div>
          <p className="text-xl font-bold text-slate-900 font-mono">{formatCurrency(totals.equity)}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-4 h-4 text-emerald-600" />
            <span className="text-sm text-slate-500">Revenue</span>
          </div>
          <p className="text-xl font-bold text-slate-900 font-mono">{formatCurrency(totals.revenue)}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-2 mb-2">
            <TrendingDown className="w-4 h-4 text-amber-600" />
            <span className="text-sm text-slate-500">Expenses</span>
          </div>
          <p className="text-xl font-bold text-slate-900 font-mono">{formatCurrency(totals.expense)}</p>
        </div>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Search accounts..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-9"
          data-testid="search-accounts"
        />
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Code</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Account Name</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Type</th>
              <th className="px-6 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Balance</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {Object.entries(groupedAccounts).map(([type, accs]) => (
              accs.length > 0 && (
                <React.Fragment key={type}>
                  <tr className="bg-slate-50/50">
                    <td colSpan={4} className="px-6 py-2 text-sm font-semibold text-slate-700 capitalize">
                      {type}s
                    </td>
                  </tr>
                  {accs.map((account) => (
                    <tr key={account.id} className="hover:bg-slate-50/50 transition-colors">
                      <td className="px-6 py-3 text-sm font-mono font-medium text-slate-900">{account.code}</td>
                      <td className="px-6 py-3 text-sm text-slate-700">{account.name}</td>
                      <td className="px-6 py-3">
                        <span className={cn(
                          'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium capitalize',
                          accountTypeColors[account.account_type]
                        )}>
                          {account.account_type}
                        </span>
                      </td>
                      <td className="px-6 py-3 text-sm text-right font-mono font-semibold text-slate-900">
                        {formatCurrency(account.balance)}
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              )
            ))}
            {filteredAccounts.length === 0 && (
              <tr>
                <td colSpan={4} className="px-6 py-12 text-center">
                  <div className="text-slate-500">
                    <BookOpen className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                    <p className="font-medium">No accounts found</p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Add Account Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Account</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="code">Account Code</Label>
                  <Input
                    id="code"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    placeholder="e.g., 1000"
                    required
                    data-testid="account-code-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="account_type">Account Type</Label>
                  <Select 
                    value={formData.account_type} 
                    onValueChange={(value) => setFormData({ ...formData, account_type: value })}
                  >
                    <SelectTrigger data-testid="account-type-select">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="asset">Asset</SelectItem>
                      <SelectItem value="liability">Liability</SelectItem>
                      <SelectItem value="equity">Equity</SelectItem>
                      <SelectItem value="revenue">Revenue</SelectItem>
                      <SelectItem value="expense">Expense</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="name">Account Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Cash"
                  required
                  data-testid="account-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description (Optional)</Label>
                <Input
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Account description..."
                  data-testid="account-description-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-account-btn">
                Add Account
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ChartOfAccounts;
